import React, { useState, useEffect, useRef } from "react";
import socket from "../connection"; // Import the central socket instance
import { useParams, useNavigate } from "react-router-dom";
import { useAuthcontext } from "../Contexts/AuthContext";
import Chat from "./Chat";
import ReactPlayer from "react-player";
import { useAppContext } from "../Contexts/AppContext";
import { manualVideos } from "./ManualVideList";

function ManualVideo() {
  const { id, room } = useParams();
  const [sessionId, setSessionId] = useState(room);
  const [player, setPlayer] = useState(null);
  const [host, setHost] = useState(null); // State to maintain the host
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [pendingSync, setPendingSync] = useState(null);

  const playerRef = useRef(null);
  const { TheatorUser } = useAuthcontext();
  const navigate = useNavigate();

  const { videoId, setVideoId } = useAppContext();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("TheatorUser"));
    console.log(user);
    if (!user) {
      navigate("/login");
    }
  }, [TheatorUser, navigate]);

  useEffect(() => {
    if (id) {
      setVideoId(id);
    }
  }, [id, setVideoId]);

  useEffect(() => {
    socket.emit("manualSessions-newuser", { sessionId, videoId });

    socket.on("manualSessions-control", (data) => {
      if (playerRef.current) {
        switch (data.action) {
          case "play":
            setPlaying(true);
            playerRef.current.seekTo(data.time, "seconds");
            break;
          case "pause":
            setPlaying(false);
            break;
          case "seek":
            playerRef.current.seekTo(data.time, "seconds");
            break;
          default:
            console.log("Unknown action");
        }
      }
    });

    socket.on("manualSessions-newUserJoined", () => {
      if (playerRef.current) {
        const currentTime = playerRef.current.getCurrentTime();
        const action = playing ? "play" : "pause";
        console.log(action);

        // Emit the current state and time
        socket.emit("manualSessions-currentState", {
          videoId,
          sessionId,
          action,
          time: currentTime,
        });
      }
    });

    // Handle new host notification
    socket.on("manualSessions-newHost", ({ newHost }) => {
      console.log(`New host selected: ${newHost}`);
      setHost(newHost);
      socket.emit("manualSessions-requestInitialPlaybackTime", { sessionId });
    });

    // Handle receiving the current state from the server
    socket.on("manualSessions-currentState", ({ action, time }) => {
      console.log(`Received current state: action=${action}, time=${time}`);
      console.log(playerRef.current);
      setPendingSync({ action, time });
      if (playerRef.current) {
        playerRef.current.seekTo(time, "seconds");
        if (action === "play") {
          setPlaying(true);
        } else if (action === "pause") {
          setPlaying(false);
        }
      }
    });

    socket.on("manualSessions-videoChange", ({ vId, action, time }) => {
      console.log(vId, action, time);
      setVideoId(vId);
      navigate(`/manualVideo/${vId}/${room}`);
      if (playerRef.current) {
        playerRef.current.seekTo(time, "seconds");
        setPlaying(action === "play");
      }
    });

    socket.on("manualSessions-changePlaybackSpeed-ans", (rate) => {
      console.log("Playback speed changed to: ", rate);
      setPlaybackRate(rate);
    });

    socket.on("manualSessions-playerMuteChange-ans", ({ isMuted }) => {
      setMuted(isMuted);
    });

    socket.on("manualSessions-playerVolumeChange-ans", ({ volume }) => {
      setVolume(volume);
    });

    return () => {
      socket.off("manualSessions-control");
      socket.off("manualSessions-newuser");
      socket.off("manualSessions-newUserJoined");
      socket.off("manualSessions-newHost");
      socket.off("manualSessions-currentState");
      socket.off("manualSessions-requestInitialPlaybackTime");
      socket.off("manualSessions-videoChange");
      socket.off("manualSessions-changePlaybackSpeed-ans");
      socket.off("manualSessions-playerMuteChange-ans");
      socket.off("manualSessions-playerVolumeChange-ans");
    };
  }, [sessionId, videoId, playing, setVideoId]);

  const onPlayerReady = () => {
    console.log("Player is ready");
    setPlayer(playerRef.current);
    if (pendingSync && playerRef.current) {
      playerRef.current.seekTo(pendingSync.time, "seconds");
      setPlaying(pendingSync.action === "play");
      setPendingSync(null);
    }
  };

  // For ReactPlayer's onProgress callback
  const handleProgress = (state) => {
    // This function is called periodically during playback
    // You can use it to sync other users if needed
  };

  // Example button handlers
  const playVideo = () => {
    const currentTime = playerRef.current?.getCurrentTime() || 0;
    setPlaying(true);
    socket.emit("manualSessions-play", { sessionId, time: currentTime, userName: TheatorUser?.name });
  };

  const pauseVideo = () => {
    const currentTime = playerRef.current?.getCurrentTime() || 0;
    setPlaying(false);
    socket.emit("manualSessions-pause", { sessionId, time: currentTime, userName: TheatorUser?.name });
  };

  const seekForward = () => {
    if (!playerRef.current) return;
    const currentTime = playerRef.current.getCurrentTime() || 0;
    const newTime = currentTime + 30;
    playerRef.current.seekTo(newTime, "seconds");
    socket.emit("manualSessions-seek", { sessionId, time: newTime, userName: TheatorUser?.name });
  };

  const seekBackward = () => {
    if (!playerRef.current) return;
    const currentTime = playerRef.current.getCurrentTime() || 0;
    const newTime = Math.max(0, currentTime - 30);
    playerRef.current.seekTo(newTime, "seconds");
    socket.emit("manualSessions-seek", { sessionId, time: newTime, userName: TheatorUser?.name });
  };

  const handleMuteChange = () => {
    const newMuteState = !muted;
    setMuted(newMuteState);
    socket.emit("manualSessions-playerMuteChange", {
      sessionId,
      isMuted: newMuteState,
    });
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    socket.emit("manualSessions-playerVolumeChange", {
      sessionId,
      volume: newVolume,
    });
  };

  const changePlaybackSpeed = (newRate) => {
    setPlaybackRate(newRate);
    socket.emit("manualSessions-changePlaybackSpeed", {
      sessionId,
      rate: newRate,
    });
  };

  // Handle player events
  const handlePlay = () => {
    if (!playing) {
      const currentTime = playerRef.current?.getCurrentTime() || 0;
      setPlaying(true);
      socket.emit("manualSessions-play", { sessionId, time: currentTime, userName: TheatorUser?.name });
    }
  };

  const handlePause = () => {
    if (playing) {
      const currentTime = playerRef.current?.getCurrentTime() || 0;
      setPlaying(false);
      socket.emit("manualSessions-pause", { sessionId, time: currentTime, userName: TheatorUser?.name });
    }
  };

  const playHandle = (vid) => {
    setVideoId(vid);
    navigate(`/manualVideo/${vid}/${room}`);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col gap-8 md:flex-row h-fit">
        <div className="w-full md:w-3/4">
          <div className="bg-black rounded-xl overflow-hidden shadow-2xl relative pt-[56.25%]">
            <ReactPlayer
              ref={playerRef}
              // url={`https://res.cloudinary.com/dgsjppp4a/video/upload/v1745656570/Shubham/${videoId}.mp4`}
              // url={`https://res.cloudinary.com/dyuov6i8c/video/upload/v1753409201/${videoId}.mp4`}
              // url="https://res.cloudinary.com/dyuov6i8c/video/upload/f_auto,q_auto/v1761475596/Dhurandhar.2025.720p.HEVC.V2.HDTC.Hindi-LiNE.x265-HDHub4u.Ms_pgu7pv.mp4"
              url="https://angelina-unintrusive-nonestimably.ngrok-free.dev/videos/movie.mp4"
              playing={playing}
              volume={volume}
              muted={muted}
              playbackRate={playbackRate}
              onReady={onPlayerReady}
              onPlay={handlePlay}
              onPause={handlePause}
              onProgress={handleProgress}
              width="100%"
              height="100%"
              controls={true}
              className="absolute top-0 left-0"
            />
          </div>
          <div className="mt-4 p-4 bg-white rounded-xl shadow-md flex justify-center flex-wrap gap-2">
            <button
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-full transition-all shadow-lg active:scale-95"
              onClick={playVideo}
            >
              Play
            </button>
            <button
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-full transition-all shadow-lg active:scale-95"
              onClick={pauseVideo}
            >
              Pause
            </button>
            <button
              className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-bold rounded-full transition-all shadow-lg active:scale-95"
              onClick={seekBackward}
            >
              -30s
            </button>
            <button
              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-full transition-all shadow-lg active:scale-95"
              onClick={seekForward}
            >
              +30s
            </button>
          </div>
        </div>
        <div className="w-full md:w-1/4 h-[500px] lg:h-[600px] max-h-[85vh]">
          <Chat />
        </div>
      </div>

      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6 pb-2 border-b-2 border-purple-500 inline-block">
          Other Movies
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {manualVideos.map((video) => (
            <div
              onClick={() => playHandle(video.videoId)}
              key={video.videoId}
              className={`bg-white cursor-pointer rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 flex flex-col overflow-hidden border-2 ${videoId === video.videoId ? 'border-purple-500 ring-2 ring-purple-200' : 'border-transparent'}`}
            >
              <div className="flex-1 flex items-center justify-center bg-gradient-to-r from-blue-100 to-purple-100 h-40 text-center text-lg font-bold text-gray-700 p-4">
                {video.title}
              </div>
              <div className="p-3 border-t text-center text-sm font-semibold text-gray-800 bg-white truncate">
                {video.title}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ManualVideo;
